import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Input } from "@/components/ui/Input/Input";
import { Textarea } from "@/components/ui/Textarea/Textarea";
import { Banner } from "@/components/auth/FormShell";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/lib/AuthContext";
import {
  useCreateSsoDomain,
  useDeleteSsoDomain,
  useDeleteSsoSaml,
  usePutSsoSaml,
  useSsoDomains,
  useSsoSaml,
  useVerifySsoDomain,
  type DomainRegistration,
  type SamlConfig,
  type SamlSp,
} from "@/lib/queries";
import { ApiError } from "@/lib/api";
import styles from "./WorkspaceSso.module.css";

/**
 * Enterprise SAML SSO settings for a workspace. Lives at
 * `/w/:ws/sso`. Owner-only on enterprise-plan workspaces — every
 * other case the API returns 403 with a specific reason, which we
 * surface as plain copy instead of a bare error toast.
 *
 * Two input modes for the IdP metadata:
 *   - `URL` (preferred) — Cognito refetches periodically.
 *   - `XML` — paste the raw metadata. One-shot; rotate by re-pasting.
 *
 * The "your SP details" panel is what the workspace owner hands to
 * their IdP team to register on the IdP side. Always shown when the
 * page renders (read-only, no copy-paste-secret content).
 */
export function WorkspaceSso() {
  const { ws } = useParams<{ ws: string }>();
  const auth = useAuth();

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/sso`} replace />;
  }

  return (
    <AppShell currentWorkspaceId={ws}>
      <WorkspaceSsoPage ws={ws} />
    </AppShell>
  );
}

function WorkspaceSsoPage({ ws }: { ws: string | undefined }) {
  // The server's saml endpoint is the single source of truth for the
  // gate (membership / role / plan); no separate useWorkspace call here
  // — AppShell already loads it for the workspace nav so the cache is
  // warm, and reading workspaceQ here without acting on it is just
  // misleading.
  const samlQ = useSsoSaml(ws);

  if (samlQ.isLoading) {
    return (
      <main className={styles.main}>
        <div className={styles.inner}>
          <p className={styles.loading}>Loading…</p>
        </div>
      </main>
    );
  }

  // The API returns 403 with a specific message for each gate path
  // (membership / role / plan). Surface those as plain copy so the
  // workspace owner knows why the page isn't usable.
  if (samlQ.error) {
    return (
      <main className={styles.main}>
        <div className={styles.inner}>
          <BackLink ws={ws} />
          <header className={styles.hero}>
            <h1 className={styles.heroTitle}>SAML SSO isn’t available here</h1>
          </header>
          <Banner variant="info" icon={<Icon name="info" size={14} />}>
            {samlQ.error instanceof ApiError ? samlQ.error.message : "Failed to load."}
          </Banner>
          {samlQ.error instanceof ApiError &&
            /enterprise/i.test(samlQ.error.message) && (
              <p className={styles.body}>
                Need SAML for your team? An <code>sm-admins</code> platform
                operator can promote this workspace to the enterprise plan
                from <Link to="/admin">/admin</Link>.
              </p>
            )}
        </div>
      </main>
    );
  }

  const config = samlQ.data?.config ?? null;
  const sp = samlQ.data?.sp ?? null;
  if (!sp) {
    return (
      <main className={styles.main}>
        <div className={styles.inner}>
          <p className={styles.loading}>No SP metadata returned — try again.</p>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.main}>
      <div className={styles.inner}>
        <BackLink ws={ws} />
        <header className={styles.hero}>
          <p className={styles.kicker}>Workspace · SAML SSO</p>
          <h1 className={styles.heroTitle}>
            Sign in with your identity provider.
          </h1>
          <p className={styles.body}>
            SAML SSO lets every member of this workspace sign in through
            your company’s existing IdP. Once you save metadata below, the
            sign-in page surfaces a “Continue with SSO” button for this
            workspace.
          </p>
        </header>

        <StatusPill config={config} />
        {config?.status === "active" && (
          <SignInUrlPanel ws={ws} />
        )}
        <SpPanel sp={sp} />
        {/* `key` on the form remounts (and re-seeds local state from
            `config`) every time the server-side row changes. Without
            this, after a successful save in XML mode, the form's
            `url` state would still hold whatever the user typed in URL
            mode earlier — flipping the mode toggle and pressing Update
            would PUT that stale URL, overwriting the XML config. */}
        <SamlForm
          ws={ws}
          config={config}
          key={config?.updatedAt ?? "fresh"}
        />
        {config?.status === "active" && <DomainsPanel ws={ws} />}
        {config && <DangerZone ws={ws} />}
      </div>
    </main>
  );
}

function BackLink({ ws }: { ws: string | undefined }) {
  return (
    <Btn
      href={`/w/${ws}`}
      variant="ghost"
      iconLeft={<Icon name="arrow-left" size={13} />}
    >
      Back to workspace
    </Btn>
  );
}

function StatusPill({ config }: { config: SamlConfig | null }) {
  const active = config?.status === "active";
  return (
    <div className={styles.statusRow}>
      <span
        className={`${styles.pill} ${active ? styles.pillActive : styles.pillPending}`}
      >
        <span className={styles.pillDot} aria-hidden="true" />
        {active ? "Configured" : "Not configured"}
      </span>
      {config && (
        <span className={styles.statusMeta}>
          Provider: <code>{config.providerName}</code> · Updated{" "}
          {new Date(config.updatedAt).toLocaleString()}
        </span>
      )}
    </div>
  );
}

/**
 * Workspace-scoped sign-in URL. Hand this to enterprise members so
 * they land on a sign-in page that surfaces the "Continue with SSO"
 * button for THIS workspace's IdP. v1 uses the workspace's hex id
 * directly; a custom-slug follow-up can replace this if needed.
 */
function SignInUrlPanel({ ws }: { ws: string | undefined }) {
  if (!ws) return null;
  const url = `${window.location.origin}/signin/${ws}`;
  return (
    <section className={styles.panel}>
      <h2 className={styles.panelTitle}>Sign-in URL for your team</h2>
      <p className={styles.panelLede}>
        Give this URL to anyone joining your workspace. It surfaces the
        "Continue with SSO" button next to the email + password form,
        wired to the identity provider configured below.
      </p>
      <code className={styles.urlBlock}>{url}</code>
    </section>
  );
}

function SpPanel({ sp }: { sp: SamlSp }) {
  return (
    <section className={styles.panel}>
      <h2 className={styles.panelTitle}>Your SP details</h2>
      <p className={styles.panelLede}>
        Hand these values to your IdP team to register a new SAML
        relying-party. They’re the same for every workspace in this
        deploy — derived from the Cognito user pool.
      </p>
      <dl className={styles.kvList}>
        <div className={styles.kvRow}>
          <dt>Entity ID / Audience</dt>
          <dd>
            <code>{sp.entityId}</code>
          </dd>
        </div>
        <div className={styles.kvRow}>
          <dt>ACS URL</dt>
          <dd>
            {sp.acsUrl ? (
              <code>{sp.acsUrl}</code>
            ) : (
              <span className={styles.warn}>
                Not configured — the Cognito Hosted UI domain isn’t set
                on this deploy. Ask an operator to deploy the stack with
                <code> COGNITO_DOMAIN</code> populated.
              </span>
            )}
          </dd>
        </div>
      </dl>
    </section>
  );
}

function SamlForm({
  ws,
  config,
}: {
  ws: string | undefined;
  config: SamlConfig | null;
}) {
  const put = usePutSsoSaml(ws);
  const [mode, setMode] = useState<"url" | "xml">(
    config?.idpMetadataXml ? "xml" : "url",
  );
  const [url, setUrl] = useState(config?.idpMetadataUrl ?? "");
  const [xml, setXml] = useState(config?.idpMetadataXml ?? "");
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    const body =
      mode === "url"
        ? { idpMetadataUrl: url.trim() }
        : { idpMetadataXml: xml.trim() };
    if (mode === "url" && !body.idpMetadataUrl) {
      setErr("Enter the metadata URL.");
      return;
    }
    if (mode === "xml" && !body.idpMetadataXml) {
      setErr("Paste the metadata XML.");
      return;
    }
    try {
      await put.mutateAsync(body);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : String(e2));
    }
  }

  return (
    <section className={styles.panel}>
      <h2 className={styles.panelTitle}>
        {config ? "Update IdP metadata" : "Add IdP metadata"}
      </h2>
      <p className={styles.panelLede}>
        Choose how to provide the metadata. URL is preferred — Cognito
        refetches it, so cert rotations on the IdP side flow through
        without redeploys.
      </p>
      <form onSubmit={onSubmit} className={styles.form} noValidate>
        <div className={styles.modeRow} role="radiogroup" aria-label="Metadata mode">
          <label className={styles.modeOption}>
            <input
              type="radio"
              name="mode"
              value="url"
              checked={mode === "url"}
              onChange={() => setMode("url")}
            />
            <span>HTTPS URL</span>
          </label>
          <label className={styles.modeOption}>
            <input
              type="radio"
              name="mode"
              value="xml"
              checked={mode === "xml"}
              onChange={() => setMode("xml")}
            />
            <span>Pasted XML</span>
          </label>
        </div>

        {mode === "url" ? (
          <Input
            type="url"
            inputMode="url"
            placeholder="https://idp.example.com/saml/metadata"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
          />
        ) : (
          <Textarea
            rows={10}
            placeholder="<EntityDescriptor xmlns=&quot;urn:oasis:names:tc:SAML:2.0:metadata&quot;>…"
            value={xml}
            onChange={(e) => setXml(e.target.value)}
            required
          />
        )}

        {err && (
          <Banner variant="danger" icon={<Icon name="info" size={14} />}>
            {err}
          </Banner>
        )}

        <Btn
          type="submit"
          variant="primary"
          size="lg"
          loading={put.isPending}
          iconRight={<Icon name="arrow-right" size={14} />}
        >
          {config ? "Update SAML" : "Enable SAML"}
        </Btn>
      </form>
    </section>
  );
}

/**
 * Email-domain claims. Each domain starts in `pending`; the owner adds a
 * DNS TXT record at `_sm-verify.<domain>` containing the displayed
 * token, then clicks Verify. The server runs an authoritative DNS
 * lookup; if it matches, the domain flips to `verified` and an
 * `attribute_not_exists` transaction guarantees no other workspace can
 * verify the same domain. Verified domains feed the sign-in page's
 * email-domain auto-detect (PR9f's discover endpoint).
 */
function DomainsPanel({ ws }: { ws: string | undefined }) {
  const domainsQ = useSsoDomains(ws);
  const create = useCreateSsoDomain(ws);
  const verify = useVerifySsoDomain(ws);
  const del = useDeleteSsoDomain(ws);
  const [input, setInput] = useState("");
  const [err, setErr] = useState<string | null>(null);

  async function onAdd(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!input.trim()) {
      setErr("Enter a domain (e.g. acme.com).");
      return;
    }
    try {
      await create.mutateAsync({ domain: input.trim() });
      setInput("");
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : String(e2));
    }
  }

  const domains = domainsQ.data?.domains ?? [];

  return (
    <section className={styles.panel}>
      <h2 className={styles.panelTitle}>Email domains</h2>
      <p className={styles.panelLede}>
        Claim email domains for this workspace. Once a domain is verified
        via DNS, users typing <code>user@yourdomain.com</code> on the
        sign-in page are routed to your IdP automatically — no slug URL
        needed.
      </p>

      <form onSubmit={onAdd} className={styles.form} noValidate>
        <div className={styles.domainAddRow}>
          <Input
            type="text"
            placeholder="acme.com"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <Btn
            type="submit"
            variant="secondary"
            loading={create.isPending}
          >
            Add domain
          </Btn>
        </div>
        {err && (
          <Banner variant="danger" icon={<Icon name="info" size={14} />}>
            {err}
          </Banner>
        )}
      </form>

      {domainsQ.isLoading && (
        <p className={styles.loading}>Loading domains…</p>
      )}
      {domains.length === 0 && !domainsQ.isLoading && (
        <p className={styles.body}>No domains registered yet.</p>
      )}
      {domains.map((d) => (
        <DomainRow
          key={d.domain}
          domain={d}
          verify={verify}
          del={del}
        />
      ))}
    </section>
  );
}

function DomainRow({
  domain,
  verify,
  del,
}: {
  domain: DomainRegistration;
  verify: ReturnType<typeof useVerifySsoDomain>;
  del: ReturnType<typeof useDeleteSsoDomain>;
}) {
  const verified = domain.status === "verified";
  const [showInstructions, setShowInstructions] = useState(!verified);
  // Per-row error so two rows acting at once don't clobber each
  // other's messages. The validator caught the prior version
  // swallowing 409s ("No TXT record at …", "value didn't match") —
  // users would click Verify and see nothing happen.
  const [rowErr, setRowErr] = useState<string | null>(null);
  // React Query's mutations are shared across rows in this panel; we
  // track which row started the most-recent click ourselves so the
  // spinner only spins on the row that's working.
  const [busy, setBusy] = useState<"verify" | "remove" | null>(null);

  async function run(
    action: "verify" | "remove",
    fn: () => Promise<unknown>,
  ): Promise<void> {
    setRowErr(null);
    setBusy(action);
    try {
      await fn();
    } catch (e) {
      setRowErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className={styles.domainRow}>
      <div className={styles.domainHeader}>
        <span className={styles.domainName}>{domain.domain}</span>
        <span
          className={`${styles.pill} ${verified ? styles.pillActive : styles.pillPending}`}
        >
          <span className={styles.pillDot} aria-hidden="true" />
          {verified ? "Verified" : "Pending DNS"}
        </span>
        <span className={styles.spacer} />
        {!verified && (
          <Btn
            variant="primary"
            size="sm"
            loading={busy === "verify"}
            disabled={!!busy}
            onClick={() =>
              void run("verify", () =>
                verify.mutateAsync({ domain: domain.domain }),
              )
            }
          >
            Verify
          </Btn>
        )}
        <Btn
          variant="ghost"
          size="sm"
          loading={busy === "remove"}
          disabled={!!busy}
          onClick={() =>
            void run("remove", () =>
              del.mutateAsync({ domain: domain.domain }),
            )
          }
        >
          Remove
        </Btn>
      </div>
      {rowErr && (
        <Banner variant="danger" icon={<Icon name="info" size={14} />}>
          {rowErr}
        </Banner>
      )}
      {!verified && showInstructions && (
        <div className={styles.dnsInstructions}>
          <p className={styles.body}>
            Add this TXT record at your DNS provider, then click <b>Verify</b>:
          </p>
          <dl className={styles.kvList}>
            <div className={styles.kvRow}>
              <dt>Host / Name</dt>
              <dd>
                <code>_sm-verify.{domain.domain}</code>
              </dd>
            </div>
            <div className={styles.kvRow}>
              <dt>Value</dt>
              <dd>
                <code>sm-verify={domain.verificationToken}</code>
              </dd>
            </div>
            <div className={styles.kvRow}>
              <dt>TTL</dt>
              <dd>300 seconds (or default)</dd>
            </div>
          </dl>
          <Btn
            variant="ghost"
            size="sm"
            onClick={() => setShowInstructions(false)}
          >
            Hide
          </Btn>
        </div>
      )}
      {!verified && !showInstructions && (
        <Btn variant="ghost" size="sm" onClick={() => setShowInstructions(true)}>
          Show DNS instructions
        </Btn>
      )}
    </div>
  );
}

function DangerZone({ ws }: { ws: string | undefined }) {
  const del = useDeleteSsoSaml(ws);
  const [err, setErr] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function onConfirm() {
    setErr(null);
    try {
      await del.mutateAsync();
      setConfirmOpen(false);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  }

  return (
    <section className={`${styles.panel} ${styles.danger}`}>
      <h2 className={styles.panelTitle}>Disable SAML</h2>
      <p className={styles.panelLede}>
        Deletes the Cognito identity provider for this workspace.
        Existing sessions keep working until their tokens expire;
        future sign-ins fall back to email + password (and Google, if
        configured).
      </p>
      {!confirmOpen ? (
        <Btn variant="destructive" onClick={() => setConfirmOpen(true)}>
          Disable SAML
        </Btn>
      ) : (
        <div className={styles.confirmRow}>
          <Btn
            variant="destructive"
            loading={del.isPending}
            onClick={() => void onConfirm()}
          >
            Confirm — disable SAML
          </Btn>
          <Btn
            variant="ghost"
            disabled={del.isPending}
            onClick={() => setConfirmOpen(false)}
          >
            Cancel
          </Btn>
        </div>
      )}
      {err && (
        <Banner variant="danger" icon={<Icon name="info" size={14} />}>
          {err}
        </Banner>
      )}
    </section>
  );
}
